(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cleanMadPrices = (root = document) => {
    const selector = ['.price', '.cart-items__price', '.cart-totals__total-value', '.cart-totals__original-value', '.cart-discounts__value'].join(',');
    const nodes = [...(root.querySelectorAll?.(selector) || [])];
    if (root.matches?.(selector)) nodes.unshift(root);
    nodes.forEach((node) => {
      if (node.children.length) return;
      const text = node.textContent.trim();
      if (!/^(?:dh\s*)?-?\s*[\d.,\s]+\s*(?:dh|MAD)$/i.test(text)) return;
      const formatted = text.replace(/^dh\s*/i, '').replace(/\s*(?:MAD|dh)$/i, '').trim().replace(/[.,]00$/, '') + ' DH';
      if (node.textContent !== formatted) node.textContent = formatted;
    });
  };

  const setupReveals = (root = document) => {
    const nodes = root.querySelectorAll?.('[data-3yla-reveal]') || [];
    if (!nodes.length || reducedMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-3yla-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -22px 0px' });
    nodes.forEach((node, index) => {
      if (node.classList.contains('is-3yla-reveal-ready')) return;
      node.classList.add('is-3yla-reveal-ready');
      node.style.setProperty('--3yla-reveal-delay', `${Math.min(index % 4, 3) * 45}ms`);
      observer.observe(node);
    });
  };

  const setupCardSwatches = (root = document) => {
    root.querySelectorAll?.('[data-3yla-card-swatch]')?.forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const card = button.closest('[data-3yla-card]');
        if (!card) return;
        card.querySelectorAll('[data-3yla-card-swatch]').forEach((item) => {
          item.classList.toggle('is-active', item === button);
          item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });
        const img = card.querySelector('[data-3yla-card-link] img');
        if (img && button.dataset.image) {
          img.removeAttribute('srcset');
          img.src = button.dataset.image;
          if (button.dataset.alt) img.alt = button.dataset.alt;
        }
        const url = button.dataset.variantUrl;
        if (url) {
          card.querySelectorAll('[data-3yla-card-link],[data-3yla-card-title-link],[data-3yla-card-cta]').forEach((link) => link.href = url);
        }
      });
    });
  };

  const setupFilters = (root = document) => {
    root.querySelectorAll?.('[data-3yla-filter-root]')?.forEach((filterRoot) => {
      if (filterRoot.dataset.filterBound === 'true') return;
      filterRoot.dataset.filterBound = 'true';
      const buttons = [...filterRoot.querySelectorAll('[data-3yla-filter]')];
      const cards = [...filterRoot.querySelectorAll('[data-3yla-card]')];
      const counter = filterRoot.querySelector('[data-3yla-visible-count]');
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const selected = (button.dataset['3ylaFilter'] || button.getAttribute('data-3yla-filter') || 'all').toLowerCase();
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          let visible = 0;
          cards.forEach((card) => {
            const tags = (card.dataset.tags || '').toLowerCase();
            const show = selected === 'all' || tags.split('|').map((x) => x.trim()).includes(selected);
            card.hidden = !show;
            if (show) visible += 1;
          });
          if (counter) counter.textContent = String(visible);
        });
      });
    });
  };

  const setupFeatured = (root = document) => {
    root.querySelectorAll?.('[data-3yla-featured]')?.forEach((section) => {
      if (section.dataset.featuredBound === 'true') return;
      section.dataset.featuredBound = 'true';
      const image = section.querySelector('.threeyla-featured__image');
      const hiddenVariant = section.querySelector('[data-3yla-featured-variant]');
      const label = section.querySelector('[data-3yla-featured-label]');
      const detailLink = section.querySelector('[data-3yla-featured-link]');
      section.querySelectorAll('[data-3yla-featured-swatch]').forEach((button) => {
        button.addEventListener('click', () => {
          section.querySelectorAll('[data-3yla-featured-swatch]').forEach((item) => {
            item.classList.toggle('is-active', item === button);
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
          });
          if (hiddenVariant && button.dataset.variantId) hiddenVariant.value = button.dataset.variantId;
          if (label && button.dataset.variantTitle) label.textContent = button.dataset.variantTitle;
          if (detailLink && button.dataset.variantUrl) detailLink.href = button.dataset.variantUrl;
          if (image && button.dataset.image) {
            image.removeAttribute('srcset');
            image.src = button.dataset.image;
            if (button.dataset.alt) image.alt = button.dataset.alt;
          }
        });
      });
    });
  };

  const boot = (root = document) => {
    cleanMadPrices(root);
    setupReveals(root);
    setupCardSwatches(root);
    setupFilters(root);
    setupFeatured(root);
  };

  const start = () => {
    boot(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target?.nodeType === Node.ELEMENT_NODE) cleanMadPrices(mutation.target);
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          boot(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();